import { useState, useEffect } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { Check, ChevronLeft, ChevronRight, Eye, AlertTriangle } from "lucide-react";
import { postTypes } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const STEPS = ["Choose Type", "Fill Details", "Add Media", "Pricing / Terms", "Review"];

// post type → { endpoint, capability, marketplace tab }
const TYPE_MAP: Record<string, { endpoint: string; cap: string; tab: string } | null> = {
  resume: { endpoint: "resume", cap: "find_work", tab: "People" },
  service: { endpoint: "service", cap: "offer_service", tab: "Services" },
  "company-service": { endpoint: "service", cap: "offer_service", tab: "Services" },
  company: { endpoint: "company", cap: "promote_company", tab: "Companies" },
  job: { endpoint: "job", cap: "post_job", tab: "Jobs" },
  project: { endpoint: "project", cap: "post_project", tab: "Projects" },
  rfq: { endpoint: "rfq", cap: "post_project", tab: "RFQs" },
  item: { endpoint: "item", cap: "offer_service", tab: "Materials" },
};

const amountLabel: Record<string, string> = {
  job: "Monthly salary (PHP)", service: "Starting price (PHP)", project: "Budget (PHP)",
  "company-service": "Starting price (PHP)", resume: "Years of experience", company: "",
  item: "Price (PHP)",
};

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const fmtSize = (n: number) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PostFlow() {
  const [, params] = useRoute("/post/:type");
  const type = params?.type || "resume";
  const current = postTypes.find((t) => t.key === type) || postTypes[0];
  const map = TYPE_MAP[type];
  const { user, setUser } = useAuth();
  const [, navigate] = useLocation();

  const [step, setStep] = useState(1);
  // QA #24: category starts unselected (no preselected "Construction").
  const [form, setForm] = useState({ title: "", category: "", location: "", description: "", tags: "", amount: "", deadline: "", negotiable: false, kind: "material", mode: "sale", unit: "", stock: "", condition: "new", rateAmount: "", ratePeriod: "day", term: "Negotiable" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneTab, setDoneTab] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [skipped, setSkipped] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [rfqItems, setRfqItems] = useState<{ name: string; qty: string; unit: string }[]>([{ name: "", qty: "1", unit: "" }]);
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { api.categories("general").then((c) => setCats(c)).catch(() => setCats([])); }, []);
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i: number, k: string, v: string) => setRfqItems((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addItem = () => setRfqItems((rows) => [...rows, { name: "", qty: "1", unit: "" }]);
  const removeItem = (i: number) => setRfqItems((rows) => rows.filter((_, j) => j !== i));
  const cleanItems = () => rfqItems.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), qty: Number(r.qty) || 1, unit: r.unit.trim() }));

  function addFiles(list: FileList | null) {
    const incoming = Array.from(list || []);
    let dropped = 0;
    setFiles((prev) => {
      const map = new Map(prev.map((f) => [f.name + f.size, f]));
      for (const f of incoming) {
        if (f.size > MAX_FILE_BYTES) { dropped++; continue; }
        map.set(f.name + f.size, f);
      }
      return Array.from(map.values());
    });
    if (dropped) setSkipped((n) => n + dropped);
  }

  function buildPayload() {
    const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    const amt = Number(String(form.amount).replace(/[^\d.]/g, "")) || 0;
    switch (type) {
      case "job":
        return { title: form.title, city: form.location, salary: amt ? `PHP ${amt.toLocaleString()}/mo` : undefined, type: "Full-time", tags };
      case "service":
      case "company-service":
        return { title: form.title, city: form.location, fromPrice: amt, company: type === "company-service", tags };
      case "project":
        return { title: form.title, category: form.category, location: form.location, budget: amt, budgetType: form.negotiable ? "Negotiable" : "Estimated", deadline: form.deadline, description: form.description };
      case "rfq":
        return { title: form.title, category: form.category, location: form.location, deadline: form.deadline, description: form.description, items: cleanItems() };
      case "resume": {
        const rateNum = Number(String(form.rateAmount).replace(/[^\d.]/g, "")) || 0;
        const priceStr = rateNum ? `₱${rateNum.toLocaleString()}/${form.ratePeriod}` : "";
        const termStr = form.term && form.term !== "Negotiable" ? form.term : "";
        const rate = [priceStr, termStr].filter(Boolean).join(" · ") || undefined;
        return { title: form.title, city: form.location, years: amt, rate, skills: tags };
      }
      case "company":
        return { name: form.title, tagline: form.description, city: form.location, tags };
      case "item":
        return {
          title: form.title, category: form.category, location: form.location,
          kind: form.kind, mode: form.mode, price: amt,
          unit: form.unit || (form.mode === "rent" ? "day" : "unit"),
          stock: Number(String(form.stock).replace(/[^\d]/g, "")) || 0,
          condition: form.condition, description: form.description, tags,
        };
      default:
        return {};
    }
  }

  const CATEGORY_TYPES = ["item", "project", "rfq"];
  const itemPrice = () => Number(String(form.amount).replace(/[^\d.]/g, "")) || 0;
  function validateStep(s: number): string | null {
    if (s === 2) {
      if (form.title.trim().length < 3) return type === "company" ? "A company name of at least 3 characters is required." : "A title of at least 3 characters is required.";
      if (CATEGORY_TYPES.includes(type) && !form.category.trim()) return "Please choose a category to continue.";
      if (type === "rfq" && cleanItems().length === 0) return "Add at least one line item to continue.";
    }
    // QA #15: an item for sale needs at least one product photo.
    if (s === 3 && type === "item" && files.length === 0) return "Add at least one product photo to continue.";
    // QA #14/#17: require a real price for an item before review.
    if (s === 4 && type === "item" && itemPrice() <= 0) return "Enter a price greater than ₱0 to continue.";
    return null;
  }
  // Mobile #8: a step is only "complete" (green check) once it actually holds data.
  function stepComplete(s: number): boolean {
    switch (s) {
      case 1: return true; // a post type is always chosen
      case 2: return form.title.trim().length >= 3 && (!CATEGORY_TYPES.includes(type) || !!form.category.trim()) && (type !== "rfq" || cleanItems().length > 0);
      case 3: return files.length > 0; // Add Media is optional — only complete if media was added
      case 4:
        if (type === "company") return true; // no pricing needed
        if (type === "item") return !!String(form.amount).trim();
        if (type === "rfq") return !!form.deadline.trim();
        if (type === "resume") return !!String(form.rateAmount).trim() || !!String(form.amount).trim();
        return !!String(form.amount).trim(); // job / service / project
      case 5: return true; // review
      default: return false;
    }
  }
  function goNext() {
    const msg = validateStep(step);
    if (msg) { setError(msg); return; }
    setError(null);
    setStep(step + 1);
  }
  function goPrev() {
    setError(null);
    setStep(Math.max(1, step - 1));
  }
  // QA #28: let the user safely abandon a post from within the wizard.
  function discard() {
    const hasInput = form.title || form.description || form.amount || files.length > 0;
    if (hasInput && !window.confirm("Discard this post? Anything you've entered will be lost.")) return;
    navigate("/dashboard");
  }

  async function publish() {
    setError(null);
    if (!user) { navigate("/login"); return; }
    if (!map) { setDoneTab("soon"); setStep(6); return; }
    if (!form.title.trim()) { setError("A title is required."); setStep(2); return; }
    if (type === "rfq" && cleanItems().length === 0) { setError("Add at least one line item."); setStep(2); return; }
    setBusy(true);
    try {
      // additive multi-role: enable the needed activity if the account doesn't have it yet
      if (!user.capabilities?.includes(map.cap)) {
        const u = await api.addActivity(map.cap);
        if (u) setUser(u);
      }
      if (type === "item") {
        const itemRes = await api.createItem(buildPayload());
        if (itemRes?.item?.id) {
          if (files.length) { try { await api.uploadItemImage(itemRes.item.id, files[0]); setUploadedCount(1); } catch { /* skip */ } }
          setDoneTab(map.tab); setStep(6);
        } else setError("Could not publish. Please try again.");
        return;
      }
      const res = await api.createListing(map.endpoint, buildPayload());
      if (res?.item) {
        // Upload any files collected in the Add Media step to the new project.
        if (type === "project" && res.item.id && files.length) {
          let ok = 0;
          for (const f of files) {
            try { await api.uploadAttachment(res.item.id, f); ok++; } catch { /* skip a failed file */ }
          }
          setUploadedCount(ok);
        }
        // Résumé photos go to the owner's portfolio (shown on the résumé).
        if (type === "resume" && files.length) {
          let ok = 0;
          for (const f of files) {
            try { await api.uploadMedia(f); ok++; } catch { /* skip a failed file */ }
          }
          setUploadedCount(ok);
        }
        setDoneTab(map.tab); setStep(6);
      } else setError("Could not publish. Please try again.");
    } catch (e: any) { setError(e?.message || "Publish failed."); }
    finally { setBusy(false); }
  }

  return (
    <div className="container-x py-6">
      {step < 6 && (
        <>
          <h1 className="text-2xl font-extrabold text-navy mb-1">Create &amp; Post</h1>
          <p className="text-mut text-sm mb-6">{current.title} — {current.sub}</p>

          {/* QA Tab3 #18: center the step indicator on mobile instead of hugging the left. */}
          <div className="flex items-center justify-center lg:justify-start mb-6 overflow-x-auto">
            {STEPS.map((s, i) => {
              const done = i + 1 < step && stepComplete(i + 1);
              return (
              <div key={s} className="flex items-center shrink-0">
                <div className={`flex items-center gap-2 ${i + 1 <= step ? "text-navy" : "text-mut"}`}>
                  <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-brand-green text-white" : i + 1 === step ? "bg-brand-red text-white" : "bg-navy-50 text-mut"}`}>
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </span>
                  <span className="text-xs font-semibold hidden sm:block">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className="w-6 sm:w-10 h-px bg-line mx-1 sm:mx-2" />}
              </div>
              );
            })}
          </div>
        </>
      )}

      <div className={step === 6 ? "max-w-lg mx-auto" : "grid lg:grid-cols-[1fr_360px] gap-4"}>
        <div className="card p-6">
          {!user && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-brand-gold/10 text-brand-gold rounded-lg p-3">
              <AlertTriangle className="w-4 h-4" /> You'll need to <Link href="/login" className="underline font-semibold">sign in</Link> to publish.
            </div>
          )}
          {step === 1 && (
            <div>
              <h3 className="font-bold text-navy mb-3">Choose post type</h3>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {postTypes.map((t) => (
                  <Link key={t.key} href={`/post/${t.key}`} className={`p-3 rounded-lg border text-left ${t.key === current.key ? "border-navy-500 bg-navy-50" : "border-line hover:border-navy-500"}`}>
                    <div className="font-semibold text-navy text-sm">{t.title}</div><div className="text-xs text-mut">{t.sub}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-bold text-navy">Fill in the details</h3>
              <div><label className="label">{type === "company" ? "Company name *" : "Title *"}</label><input className="field" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Enter a clear, specific title" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="label">Category{CATEGORY_TYPES.includes(type) && <span className="text-brand-red"> *</span>}</label><select className="field" value={form.category} onChange={(e) => set("category", e.target.value)}><option value="">Select a category…</option>{(cats.length ? cats.map((c) => c.name) : ["Construction", "Professional", "IT & Digital", "Home & Personal", "Logistics", "Business"]).map((n) => <option key={n}>{n}</option>)}<option value="Other">Other</option></select></div>
                <div><label className="label">Location</label><input className="field" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / Province" /></div>
              </div>
              {type === "item" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="label">Item type</label><select className="field" value={form.kind} onChange={(e) => set("kind", e.target.value)}><option value="material">Material / Supply</option><option value="equipment">Equipment / Tool</option></select></div>
                  <div><label className="label">Condition</label><select className="field" value={form.condition} onChange={(e) => set("condition", e.target.value)}><option value="new">Brand New</option><option value="used">Used / Second-hand</option></select></div>
                </div>
              )}
              <div><label className="label">Description</label><textarea className="field h-28 py-2" maxLength={2000} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe your post in detail…" /><p className="text-[11px] text-mut text-right mt-0.5">{form.description.length}/2000 characters</p></div>
              {type === "rfq" ? (
                <div>
                  <label className="label">Line items *</label>
                  <div className="space-y-2">
                    {rfqItems.map((row, i) => (
                      <div key={i} className="flex gap-2">
                        <input className="field flex-1" value={row.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Item (e.g. Portland Cement)" />
                        <input className="field w-20" inputMode="numeric" value={row.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="Qty" />
                        <input className="field w-24" value={row.unit} onChange={(e) => setItem(i, "unit", e.target.value)} placeholder="unit" />
                        <button type="button" onClick={() => removeItem(i)} disabled={rfqItems.length === 1} className="btn-outline h-10 px-2 text-xs text-brand-red disabled:opacity-40">✕</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addItem} className="btn-outline mt-2 h-9 text-xs">+ Add item</button>
                </div>
              ) : (
                <div><label className="label">{type === "resume" ? "Skills (comma-separated)" : "Tags (comma-separated)"}</label><input className="field" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="e.g. Construction, Project Management" /></div>
              )}
            </div>
          )}
          {step === 3 && (
            <div>
              <h3 className="font-bold text-navy mb-3">Add media</h3>
              {type === "item" ? (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = Array.from(e.dataTransfer.files || []).find((x) => x.type.startsWith("image/")); if (f && f.size <= MAX_FILE_BYTES) setFiles([f]); else if (f) setSkipped((n) => n + 1); }}
                    className={`border-2 border-dashed rounded-xl p-10 text-center text-sm transition-colors ${dragOver ? "border-brand-red bg-brand-red/5" : "border-line"}`}
                  >
                    <input id="item-photo" type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size <= MAX_FILE_BYTES) setFiles([f]); else if (f) setSkipped((n) => n + 1); e.target.value = ""; }} />
                    <p className="text-mut">Drag &amp; drop a product photo here</p>
                    <p className="text-xs text-mut mt-1">JPEG, PNG, or WebP · up to 25 MB · one photo</p>
                    <label htmlFor="item-photo" className="btn-outline inline-flex mt-3 cursor-pointer">Choose photo</label>
                  </div>
                  {skipped > 0 && <p className="text-xs text-brand-red mt-2">Photo skipped — use an image under 25 MB.</p>}
                  {files[0] && (
                    <div className="mt-3 flex items-center gap-3 border border-line rounded-lg p-3">
                      <img src={URL.createObjectURL(files[0])} alt="preview" className="w-16 h-16 rounded object-cover" />
                      <span className="text-sm text-navy truncate flex-1">{files[0].name} <span className="text-mut">· {fmtSize(files[0].size)}</span></span>
                      <button onClick={() => setFiles([])} className="text-brand-red text-xs shrink-0">Remove</button>
                    </div>
                  )}
                  <p className="text-xs text-mut mt-2">At least one clear product photo is required<span className="text-brand-red"> *</span> — it helps your item sell.</p>
                </>
              ) : type === "project" ? (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                    className={`border-2 border-dashed rounded-xl p-10 text-center text-sm transition-colors ${dragOver ? "border-brand-red bg-brand-red/5" : "border-line"}`}
                  >
                    <input id="post-media" type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <p className="text-mut">Drag &amp; drop images or documents here</p>
                    <p className="text-xs text-mut mt-1">PDF, images, or Office docs · up to 25 MB each</p>
                    <label htmlFor="post-media" className="btn-outline inline-flex mt-3 cursor-pointer">Choose files</label>
                  </div>
                  {skipped > 0 && <p className="text-xs text-brand-red mt-2">{skipped} file{skipped === 1 ? "" : "s"} skipped (over 25 MB).</p>}
                  {files.length > 0 && (
                    <ul className="mt-3 divide-y divide-line border border-line rounded-lg px-3">
                      {files.map((f, i) => (
                        <li key={f.name + f.size} className="flex items-center justify-between py-2 text-sm gap-3">
                          <span className="text-navy truncate">{f.name} <span className="text-mut">· {fmtSize(f.size)}</span></span>
                          <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-brand-red text-xs shrink-0">Remove</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-mut mt-2">Files attach to your project as soon as you publish.</p>
                </>
              ) : type === "resume" ? (
                <>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const imgs = Array.from(e.dataTransfer.files || []).filter((x) => x.type.startsWith("image/")); const dt = new DataTransfer(); imgs.forEach((f) => dt.items.add(f)); addFiles(dt.files); }}
                    className={`border-2 border-dashed rounded-xl p-10 text-center text-sm transition-colors ${dragOver ? "border-brand-red bg-brand-red/5" : "border-line"}`}
                  >
                    <input id="resume-media" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <p className="text-mut">Add portfolio photos of your past work</p>
                    <p className="text-xs text-mut mt-1">JPEG, PNG, or WebP · up to 25 MB each · optional</p>
                    <label htmlFor="resume-media" className="btn-outline inline-flex mt-3 cursor-pointer">Choose photos</label>
                  </div>
                  {skipped > 0 && <p className="text-xs text-brand-red mt-2">{skipped} file{skipped === 1 ? "" : "s"} skipped (over 25 MB).</p>}
                  {files.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {files.map((f, i) => (
                        <div key={f.name + f.size} className="relative aspect-square rounded-lg overflow-hidden border border-line">
                          <img src={URL.createObjectURL(f)} alt="preview" className="w-full h-full object-cover" />
                          <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full w-6 h-6 text-brand-red text-xs font-bold shadow">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-mut mt-2">Photos are added to your portfolio and shown on your résumé — optional, you can add more later.</p>
                </>
              ) : (
                <div className="border-2 border-dashed border-line rounded-xl p-10 text-center text-sm text-mut">
                  Media attachments are available on <span className="font-semibold text-navy">projects</span> and <span className="font-semibold text-navy">résumés</span>.<br />
                  <span className="text-xs">This step doesn&apos;t apply to a {current.title.toLowerCase()} — continue.</span>
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-bold text-navy">Pricing / Terms</h3>
              {type === "company" ? (
                <p className="text-sm text-mut">No pricing needed for a company page — continue.</p>
              ) : type === "item" ? (
                <>
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-2"><input type="radio" checked={form.mode === "sale"} onChange={() => set("mode", "sale")} /> For Sale</label>
                    <label className="flex items-center gap-2"><input type="radio" checked={form.mode === "rent"} onChange={() => set("mode", "rent")} /> For Rent</label>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><label className="label">{form.mode === "rent" ? "Price per day (PHP)" : "Price (PHP)"}</label><input className="field" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="e.g. 260" /></div>
                    <div><label className="label">Unit</label><input className="field" value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder={form.mode === "rent" ? "day" : "e.g. bag, pc"} /></div>
                    <div><label className="label">Stock / Qty available</label><input className="field" inputMode="numeric" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="e.g. 100" /></div>
                  </div>
                  <p className="text-xs text-mut">Buyers pay from their KontrataPH wallet; funds are held in escrow and released to you when the buyer confirms receipt.</p>
                </>
              ) : type === "rfq" ? (
                <>
                  <p className="text-sm text-mut">Suppliers will quote per line item — no price needed from you. Just set a closing date.</p>
                  <div><label className="label">Quote deadline</label><input type="date" className="field" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></div>
                </>
              ) : type === "resume" ? (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">Rate / Pricing (PHP)</label>
                      <input className="field" inputMode="numeric" value={form.rateAmount} onChange={(e) => set("rateAmount", e.target.value)} placeholder="e.g. 600" />
                    </div>
                    <div>
                      <label className="label">Per</label>
                      <select className="field" value={form.ratePeriod} onChange={(e) => set("ratePeriod", e.target.value)}>
                        <option value="hour">Hour</option>
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="project">Project</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label">Engagement term</label>
                    <select className="field" value={form.term} onChange={(e) => set("term", e.target.value)}>
                      <option value="Negotiable">Negotiable</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Per project">Per project</option>
                    </select>
                  </div>
                  <div><label className="label">Years of experience</label><input className="field" inputMode="numeric" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="e.g. 8" /></div>
                  <p className="text-xs text-mut">Your rate and engagement term show on your résumé so clients know what to expect before they reach out.</p>
                </>
              ) : (
                <>
                  {type === "project" && (
                    <div className="flex gap-4 text-sm"><label className="flex items-center gap-2"><input type="radio" checked={!form.negotiable} onChange={() => set("negotiable", false)} /> Fixed / Estimated</label><label className="flex items-center gap-2"><input type="radio" checked={form.negotiable} onChange={() => set("negotiable", true)} /> Negotiable</label></div>
                  )}
                  <div><label className="label">{amountLabel[type] || "Amount"}</label><input className="field" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="e.g. 50,000" /></div>
                  {(type === "project" || type === "job") && (
                    <div><label className="label">Deadline</label><input type="date" className="field" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></div>
                  )}
                </>
              )}
            </div>
          )}
          {step === 5 && (
            <div><h3 className="font-bold text-navy mb-3">Review</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-line py-2"><span className="text-mut">Type</span><span className="font-semibold text-navy">{current.title}</span></div>
                <div className="flex justify-between border-b border-line py-2"><span className="text-mut">{type === "company" ? "Name" : "Title"}</span><span className="font-semibold text-navy">{form.title || "—"}</span></div>
                <div className="flex justify-between border-b border-line py-2"><span className="text-mut">Location</span><span className="font-semibold text-navy">{form.location || "—"}</span></div>
                {form.amount && <div className="flex justify-between border-b border-line py-2"><span className="text-mut">{amountLabel[type]}</span><span className="font-semibold text-navy">{form.amount}</span></div>}
                <div className="py-2"><span className="text-mut">Description</span><p className="text-navy mt-1">{form.description || "—"}</p></div>
              </div>
              {error && <p className="text-sm text-brand-red mt-3">{error}</p>}
            </div>
          )}
          {step === 6 && (
            <div className="text-center py-8">
              {doneTab === "soon" ? (
                <>
                  <div className="w-14 h-14 rounded-full bg-brand-gold/10 grid place-items-center mx-auto mb-3"><AlertTriangle className="w-7 h-7 text-brand-gold" /></div>
                  <h3 className="font-bold text-navy text-lg">Coming soon</h3>
                  <p className="text-sm text-mut mt-1 max-w-sm mx-auto">{current.title} lands in a later increment. The other post types are live now.</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-brand-green/10 grid place-items-center mx-auto mb-3"><Check className="w-7 h-7 text-brand-green" /></div>
                  <h3 className="font-bold text-navy text-lg">Published! 🎉</h3>
                  <p className="text-sm text-mut mt-1 max-w-sm mx-auto">
                    Your post is live in the marketplace and shows on your dashboard.
                    {uploadedCount > 0 && ` ${uploadedCount} file${uploadedCount === 1 ? "" : "s"} attached.`}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
                    <Link href={`/marketplace?tab=${doneTab}`} className="btn-red w-full sm:w-auto">View Marketplace</Link>
                    <Link href="/dashboard" className="btn-outline w-full sm:w-auto">Go to Dashboard</Link>
                  </div>
                </>
              )}
            </div>
          )}

          {step < 6 && (
            <>
              {step < 5 && error && <p className="text-sm text-brand-red mt-4 flex items-center gap-1"><AlertTriangle className="w-4 h-4 shrink-0" />{error}</p>}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-line">
                <div className="flex items-center gap-2">
                  <button onClick={goPrev} className="btn-outline" disabled={step === 1}><ChevronLeft className="w-4 h-4" />Previous</button>
                  {step > 1 && <button onClick={discard} type="button" className="btn-ghost text-mut hover:text-brand-red text-sm">Discard</button>}
                </div>
                {step === 5 ? (
                  <button onClick={publish} disabled={busy} className="btn-red disabled:opacity-60">{busy ? "Publishing…" : "Publish"}<ChevronRight className="w-4 h-4" /></button>
                ) : (
                  <button onClick={goNext} className="btn-red">Next<ChevronRight className="w-4 h-4" /></button>
                )}
              </div>
            </>
          )}
        </div>

        {step < 6 && (
        <div className="card p-5 h-fit">
          <div className="flex items-center gap-2 text-mut text-sm mb-3"><Eye className="w-4 h-4" />Preview</div>
          <div className="border border-line rounded-lg overflow-hidden">
            {/* QA #26/#27: preview reflects the uploaded image, price and sale/rent mode. */}
            {type === "item" && files[0] && (
              <img src={URL.createObjectURL(files[0])} alt="Item preview" className="w-full h-40 object-cover" />
            )}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="chip bg-navy-50 text-navy-600">{current.title.replace("Post ", "").replace("Create a ", "")}</span>
                {type === "item" && <span className={`chip ${form.mode === "rent" ? "bg-brand-gold/15 text-brand-gold" : "bg-brand-green/15 text-brand-green"}`}>{form.mode === "rent" ? "For Rent" : "For Sale"}</span>}
              </div>
              <h4 className="font-bold text-navy">{form.title || "Sample title"}</h4>
              {(form.category || form.location) && <p className="text-xs text-mut mt-1">{[form.category, form.location].filter(Boolean).join(" · ")}</p>}
              {type === "item" && itemPrice() > 0 && (
                <p className="text-brand-red font-bold mt-1">₱{itemPrice().toLocaleString()}{form.mode === "rent" ? `/${form.unit || "day"}` : ""}</p>
              )}
              <p className="text-sm text-mut mt-1">{form.description || "Your description preview will appear here as you type."}</p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-line text-xs text-mut"><div className="w-7 h-7 rounded-full bg-navy-50" /> {user ? `${user.firstName} ${user.lastName}` : "Posted by you"}</div>
            </div>
          </div>
          <p className="text-[11px] text-mut mt-3 text-center">All posts are reviewed to keep the marketplace safe and trusted.</p>
        </div>
        )}
      </div>
    </div>
  );
}
